import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, FileText, Send, CheckCircle2, AlertTriangle, Download, Users, Settings as SettingsIcon, X, Eye, Building2 } from 'lucide-react';
import BillingProfileSetup from '../../components/BillingProfileSetup';
import InvoiceCreateModal from '../../components/InvoiceCreateModal';
import InvoiceDetailModal from '../../components/InvoiceDetailModal';
import CustomerManagerModal from '../../components/CustomerManagerModal';

const STATUS_META = {
  draft:               { label: 'Utkast',          color: 'gray',   icon: FileText },
  awaiting_signature:  { label: 'Inväntar signering', color: 'amber', icon: AlertTriangle },
  sent:                { label: 'Skickad',         color: 'blue',   icon: Send },
  paid:                { label: 'Betald',          color: 'green',  icon: CheckCircle2 },
  overdue:             { label: 'Förfallen',       color: 'red',    icon: AlertTriangle },
  cancelled:           { label: 'Makulerad',       color: 'gray',   icon: X },
};

const COLOR = {
  gray:  'bg-muted text-muted-foreground border-border',
  amber: 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900',
  blue:  'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900',
  green: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-900',
  red:   'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900',
};

const fmtKr = (v) => v != null ? `${Number(v).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr` : '—';

const InvoiceDrafts = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [completeness, setCompleteness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showCustomers, setShowCustomers] = useState(false);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [autoOpenSign, setAutoOpenSign] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, compRes] = await Promise.all([
        api.listInvoices(),
        api.getBillingCompleteness(),
      ]);
      setInvoices(invRes.data);
      setCompleteness(compRes.data);
    } catch (err) {
      toast.error('Kunde inte hämta fakturor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateClick = () => {
    if (!completeness?.complete) {
      setShowProfileSetup(true);
      return;
    }
    setShowCreate(true);
  };

  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);

  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
    totalOutstanding: invoices.filter(i => ['sent', 'overdue'].includes(i.status)).reduce((s, i) => s + (i.total || 0), 0),
  };

  return (
    <div className="space-y-5" data-testid="invoicing-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <FileText size={24} className="text-[#E8613A]" /> Fakturor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Skapa, signera med BankID och skicka fakturor till dina kunder.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowCustomers(true)} className="rounded-full h-9" data-testid="open-customers-btn">
            <Users size={14} className="mr-1.5" /> Kunder
          </Button>
          <Button variant="outline" onClick={() => setShowProfileSetup(true)} className="rounded-full h-9" data-testid="open-billing-profile-btn">
            <SettingsIcon size={14} className="mr-1.5" /> Företagsuppgifter
          </Button>
          <Button onClick={handleCreateClick} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full h-9" data-testid="new-invoice-btn">
            <Plus size={14} className="mr-1.5" /> Ny faktura
          </Button>
        </div>
      </div>

      {/* Onboarding banner if profile not complete */}
      {completeness && !completeness.complete && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-xl p-4 flex items-start gap-3" data-testid="profile-onboarding-banner">
          <AlertTriangle size={18} className="text-amber-700 dark:text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-amber-900 dark:text-amber-200">Komplettera dina företagsuppgifter</h3>
            <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
              Innan du kan skicka fakturor behöver du fylla i:
              <b> {(completeness.missing_fields || []).join(', ')}</b>
              {!completeness.has_logo && ', ladda upp en logga'}.
            </p>
          </div>
          <Button size="sm" onClick={() => setShowProfileSetup(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="complete-profile-btn">
            Slutför
          </Button>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Utestående" value={fmtKr(stats.totalOutstanding)} accent="orange" testid="stat-outstanding" />
        <StatCard label="Skickade" value={stats.sent} accent="blue" testid="stat-sent" />
        <StatCard label="Betalda" value={stats.paid} accent="green" testid="stat-paid" />
        <StatCard label="Förfallna" value={stats.overdue} accent="red" testid="stat-overdue" />
        <StatCard label="Utkast" value={stats.draft} accent="gray" testid="stat-draft" />
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        {[
          { v: 'all', l: `Alla (${stats.total})` },
          { v: 'draft', l: 'Utkast' },
          { v: 'awaiting_signature', l: 'Inväntar signering' },
          { v: 'sent', l: 'Skickade' },
          { v: 'overdue', l: 'Förfallna' },
          { v: 'paid', l: 'Betalda' },
          { v: 'cancelled', l: 'Makulerade' },
        ].map(f => (
          <button key={f.v}
            onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === f.v ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-card text-muted-foreground border-border hover:border-[#E8613A]/50'
            }`}
            data-testid={`filter-${f.v}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-sm text-muted-foreground p-6 text-center">Laddar...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-xl p-10 text-center">
          <FileText size={36} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">Inga fakturor här. Tryck "Ny faktura" för att börja.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Fakturanr</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Kund</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Datum</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Förfaller</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">Belopp</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map(inv => {
                  const meta = STATUS_META[inv.status] || STATUS_META.draft;
                  const Icon = meta.icon;
                  const cust = inv.customer_snapshot || {};
                  return (
                    <tr key={inv.id} className="hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => { setAutoOpenSign(false); setDetailInvoice(inv); }} data-testid={`row-${inv.id}`}>
                      <td className="px-4 py-3 font-bold text-foreground">{inv.invoice_number}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium truncate max-w-[200px]">{cust.company_name || cust.contact_person || '—'}</p>
                        {cust.org_number && <p className="text-[10px] text-muted-foreground">Org.nr {cust.org_number}</p>}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{inv.issue_date}</td>
                      <td className="px-4 py-3 text-muted-foreground">{inv.due_date}</td>
                      <td className="px-4 py-3 text-right font-bold">{fmtKr(inv.total)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-bold ${COLOR[meta.color]}`}>
                          <Icon size={10} /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-3">
                          {(inv.status === 'draft' || inv.status === 'awaiting_signature') && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setAutoOpenSign(true); setDetailInvoice(inv); }}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#E8613A] hover:bg-[#D4532F] px-2.5 py-1 rounded-full"
                              data-testid={`send-btn-${inv.id}`}
                            >
                              <Send size={11} /> Skicka
                            </button>
                          )}
                          <a href={api.invoicePdfUrl(inv.id)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-[#E8613A] hover:underline" data-testid={`pdf-${inv.id}`}>
                            <Download size={12} /> PDF
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && <InvoiceCreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchAll(); }} />}
      {showCustomers && <CustomerManagerModal onClose={() => setShowCustomers(false)} />}
      {showProfileSetup && (
        <BillingProfileSetup
          onClose={() => setShowProfileSetup(false)}
          onSaved={() => { setShowProfileSetup(false); fetchAll(); }}
        />
      )}
      {detailInvoice && (
        <InvoiceDetailModal
          invoiceId={detailInvoice.id}
          autoOpenSign={autoOpenSign}
          onClose={() => { setDetailInvoice(null); setAutoOpenSign(false); }}
          onChanged={() => { setDetailInvoice(null); setAutoOpenSign(false); fetchAll(); }}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value, accent, testid }) => {
  const colors = {
    orange: 'bg-[#E8613A]/10 text-[#E8613A] border-[#E8613A]/30',
    blue: 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900',
    green: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-900',
    red: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900',
    gray: 'bg-muted text-muted-foreground border-border',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[accent]}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</p>
      <p className="text-lg sm:text-xl font-bold mt-1">{value}</p>
    </div>
  );
};

export default InvoiceDrafts;
